import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Archive, CalendarCheck, CheckCircle, CircleDashed, Clock, EnvelopeSimple, FileText, FolderSimple, GearSix, Megaphone,
  NotePencil, PaperPlaneRight, PaperPlaneTilt, Plus, Tray, type Icon,
  Trash, Hourglass,
} from '@phosphor-icons/react';
import { supabase } from '../../../lib/supabase/client';
import { alsModulListe, isItemVisible, type VisibilityContext } from '../../../lib/sidebar/navigation';
import { useEmailNav, type EmailNavState } from '../../../pages/admin/emails/EmailNavContext';
import { useSidebarDaten } from '../../admin/useSidebarDaten';
import { SIDEBAR_CATEGORIES, TOP_LEVEL_ITEMS } from '../../admin/sidebarConfig';
import { ImmoButton } from '../ui/Button';
import { NavZeile } from './NavZeile';
import { SidebarDock } from './SidebarDock';
import { istZiehOrdner, objektOrdnerLabel, ordnerZaehler, sichtbareOrdner, type FesterOrdner } from './emailOrdner';

const ORDNER_ICON: Record<FesterOrdner, Icon> = {
  posteingang: Tray, offen: CircleDashed, erledigt: CheckCircle, wiedervorlage: Clock, rueckmeldung: Hourglass, gesendet: PaperPlaneTilt,
  geplant: CalendarCheck, entwuerfe: NotePencil, werbung: Megaphone, archiviert: Archive, geloescht: Trash, vorlagen: FileText,
  serienmail: PaperPlaneRight, einstellungen: GearSix,
};

const ABSCHNITT: React.CSSProperties = { fontSize: 10, fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--ink3)', padding: '10px 9px 4px' };
const ALLE_ITEMS = [...TOP_LEVEL_ITEMS, ...SIDEBAR_CATEGORIES.flatMap(c => c.items)];

/** Ist das Modul „Serienmail“ für diesen Nutzer sichtbar (gleiche Regel wie die Navigation)? */
export function useSerienmailErlaubt(userRole?: string | null, allowedModules?: unknown): boolean {
  const { activeModules } = useSidebarDaten();
  return useMemo(() => {
    const rolle = (userRole ?? '').toLowerCase();
    const ctx: VisibilityContext = { isSuperAdmin: rolle === 'super admin', isTeam: rolle === 'team', activeModules, allowedModules: alsModulListe(allowedModules) };
    const meta = ALLE_ITEMS.find(i => i.id === 'serienmail');
    return meta ? isItemVisible(meta, ctx) : false;
  }, [userRole, allowedModules, activeModules]);
}

/** Zähler am Ordner „Serienmail“: Läufe mit Status „laeuft“ (README §8). */
export function useLaufendeSerienmails(aktiv: boolean, aktualisieren: unknown): number {
  const [n, setN] = useState(0);
  useEffect(() => {
    if (!aktiv) { setN(0); return; }
    let lebt = true;
    supabase.from('serienmails').select('id', { count: 'exact', head: true }).eq('status', 'laeuft')
      .then(({ count }) => { if (lebt) setN(count ?? 0); });
    return () => { lebt = false; };
  }, [aktiv, aktualisieren]);
  return n;
}

/** Postfach, Ordner und Objekte (Sidebar und mobiles Ordner-Sheet). Objekte sind Drop-Ziel für Mails (Spec 2026-07-21). */
export function EmailOrdnerNav({ nav, serienmailErlaubt, laufend, onGewaehlt, mitPostfach = true, objekteMaxHoehe = 260, zeilenHoehe }: {
  nav: EmailNavState;
  serienmailErlaubt: boolean;
  laufend: number;
  /** Nach einer Auswahl (Sheet schließen). */
  onGewaehlt?: () => void;
  mitPostfach?: boolean;
  /** null = keine eigene Scrollfläche: die Liste ist voll lang, der Behälter scrollt (Sidebar, #540). */
  objekteMaxHoehe?: number | null;
  /** Mobil 44 px (Hit-Target). */
  zeilenHoehe?: number;
}) {
  const [dragOverId, setDragOverId] = useState<string | null>(null);
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const autoScroll = useRef<{ dir: number; raf: number }>({ dir: 0, raf: 0 });
  const stoppe = () => {
    autoScroll.current.dir = 0;
    if (autoScroll.current.raf) { cancelAnimationFrame(autoScroll.current.raf); autoScroll.current.raf = 0; }
  };
  useEffect(() => () => stoppe(), []);
  // Beim Ziehen am oberen/unteren Rand der Objektliste automatisch scrollen (wie bisher).
  // Ohne eigene Scrollfläche (Sidebar) scrollt der umgebende Behälter — dort am Rand schieben.
  const scrollZiel = (): HTMLElement | null => {
    let el: HTMLElement | null = scrollRef.current;
    while (el && el.scrollHeight <= el.clientHeight + 1) el = el.parentElement;
    return el;
  };
  const randScroll = (e: React.DragEvent) => {
    const c = scrollZiel();
    if (!c) return;
    const r = c.getBoundingClientRect();
    autoScroll.current.dir = e.clientY < r.top + 44 ? -1 : e.clientY > r.bottom - 44 ? 1 : 0;
    if (autoScroll.current.dir !== 0 && !autoScroll.current.raf) {
      const schritt = () => {
        const el = scrollZiel();
        if (!el || autoScroll.current.dir === 0) { autoScroll.current.raf = 0; return; }
        el.scrollTop += autoScroll.current.dir * 10;
        autoScroll.current.raf = requestAnimationFrame(schritt);
      };
      autoScroll.current.raf = requestAnimationFrame(schritt);
    }
  };

  return (
    <>
      {mitPostfach && <EmailOrdnerNavPostfach nav={nav} onGewaehlt={onGewaehlt} />}

      <nav aria-label="E-Mail-Ordner" className="flex flex-col" style={{ gap: 1 }}>
        <span style={ABSCHNITT}>Ordner</span>
        {sichtbareOrdner(serienmailErlaubt).map(o => {
          const zeile = (
            <NavZeile art="objekt" icon={ORDNER_ICON[o.key]} label={o.label} aktiv={nav.activeTab === o.key} minHoehe={zeilenHoehe}
              anzahl={ordnerZaehler(o.key, nav, laufend)} onClick={() => { nav.onSelectTab(o.key); onGewaehlt?.(); }} />
          );
          // #578: Posteingang/Offen/Erledigt/Werbung/Archiviert nehmen gezogene Mails an (nicht der Ordner, in dem man gerade ist).
          if (!nav.onDropInOrdner || !istZiehOrdner(o.key) || nav.activeTab === o.key) return <div key={o.key}>{zeile}</div>;
          const ziel = dragOverId === `ordner:${o.key}`;
          return (
            <div key={o.key} style={{ borderRadius: 11, boxShadow: ziel ? 'inset 0 0 0 2px var(--acc)' : 'none', background: ziel ? 'var(--acc-soft)' : undefined }}
              onDragOver={e => { if (!e.dataTransfer.types.includes('application/email-id')) return; e.preventDefault(); e.dataTransfer.dropEffect = 'move'; setDragOverId(`ordner:${o.key}`); }}
              onDragLeave={() => setDragOverId(cur => (cur === `ordner:${o.key}` ? null : cur))}
              onDrop={e => {
                e.preventDefault(); setDragOverId(null);
                let ids: string[] = [];
                try { ids = JSON.parse(e.dataTransfer.getData('application/email-ids') || '[]'); } catch { ids = []; }
                const einzel = e.dataTransfer.getData('application/email-id');
                if (ids.length === 0 && einzel) ids = [einzel];
                if (ids.length > 0) nav.onDropInOrdner?.(ids, o.key);
              }}>
              {zeile}
            </div>
          );
        })}
      </nav>

      {nav.objekte.length > 0 && (
        <div className={`flex flex-col ${objekteMaxHoehe === null ? 'shrink-0' : 'min-h-0'}`} style={{ gap: 1 }}>
          <span style={ABSCHNITT}>Objekte</span>
          <div ref={scrollRef} className={`flex flex-col ${objekteMaxHoehe === null ? '' : 'immo-scroll overflow-y-auto'}`}
            style={{ gap: 1, maxHeight: objekteMaxHoehe ?? undefined, paddingRight: 2 }}
            onDragOver={randScroll} onDragLeave={e => { if (!scrollRef.current?.contains(e.relatedTarget as Node)) stoppe(); }} onDrop={stoppe}>
            {nav.objekte.map(o => {
              const aktiv = nav.activeTab === 'objekt' && nav.selectedObjektId === o.id;
              const ziel = dragOverId === o.id;
              const label = objektOrdnerLabel(o);
              return (
                <button key={o.id} type="button" title={label} aria-current={aktiv ? 'page' : undefined}
                  onClick={() => { nav.onSelectObjekt(o.id); onGewaehlt?.(); }}
                  onDragOver={e => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; setDragOverId(o.id); }}
                  onDragLeave={() => setDragOverId(cur => (cur === o.id ? null : cur))}
                  onDrop={e => { e.preventDefault(); const id = e.dataTransfer.getData('application/email-id'); setDragOverId(null); if (id) nav.onAssignObjekt(id, o.id); }}
                  className="immo-hover-flaeche flex items-center w-full text-left cursor-pointer"
                  style={{ gap: 9, minHeight: zeilenHoehe ?? 34, padding: '0 9px', borderRadius: 11, border: 'none', fontSize: 12, fontWeight: aktiv ? 800 : 700, color: aktiv ? 'var(--acc)' : 'var(--ink)',
                    background: ziel || aktiv ? 'var(--acc-soft)' : 'transparent', boxShadow: ziel ? 'inset 0 0 0 2px var(--acc)' : 'none' }}>
                  <FolderSimple weight={aktiv ? 'fill' : 'regular'} size={16} color={aktiv ? 'var(--acc)' : 'var(--ink2)'} style={{ flexShrink: 0 }} />
                  <span className="flex-1 min-w-0 truncate">{label}</span>
                  {o.unread > 0 && (
                    <span className="immo-zahl flex items-center justify-center shrink-0" style={{ minWidth: 18, height: 18, padding: '0 5px', borderRadius: 9, fontSize: 10, fontWeight: 800, background: 'var(--acc)', color: 'var(--acc-ink)' }}>{o.unread}</span>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </>
  );
}

/** E-Mail-Modus der Sidebar (Handoff E-Mails §6, Screen 01): Postfach, „Neue E-Mail“, Ordner, Objekte; unten das Dock. */
export function EmailModus({ userRole, allowedModules, onNavigate }: { userRole?: string; allowedModules?: unknown; onNavigate: (pfad: string) => void }) {
  const { nav } = useEmailNav();
  const serienmailErlaubt = useSerienmailErlaubt(userRole, allowedModules);
  const laufend = useLaufendeSerienmails(serienmailErlaubt, nav?.activeTab);
  return (
    <div className="immo-side-in flex flex-col flex-1 min-h-0" style={{ gap: 10 }}>
      {/* #540: Wie in Outlook scrollt die ganze Leiste (sichtbarer Balken); die Objektliste ist voll lang,
          statt in einem eigenen Kästchen auf zwei Zeilen zusammengedrückt zu werden. */}
      <div className="immo-scroll flex flex-col flex-1 min-h-0 overflow-y-auto" style={{ gap: 6, margin: '0 -4px', padding: '0 4px' }}>
        {nav ? (
          <EmailOrdnerNav nav={nav} serienmailErlaubt={serienmailErlaubt} laufend={laufend} mitPostfach={false} objekteMaxHoehe={null} />
        ) : (
          <div className="flex flex-col" style={{ gap: 6 }} aria-busy="true">
            {Array.from({ length: 8 }, (_, i) => <div key={i} className="animate-pulse" style={{ height: 30, borderRadius: 11, background: 'var(--fill2)' }} />)}
          </div>
        )}
      </div>
      <SidebarDock aktiv="emails" onNavigate={onNavigate} />
    </div>
  );
}

/** Kopf der Sidebar im E-Mail-Modus: Postfach-Karte und „Neue E-Mail“ (stehen über den Ordnern, scrollen nicht mit). */
export function EmailModusKopf() {
  const { nav } = useEmailNav();
  if (!nav) return null;
  return (
    <div className="flex flex-col" style={{ gap: 10 }}>
      <EmailOrdnerNavPostfach nav={nav} />
      <ImmoButton art="akzent" groesse={44} icon={Plus} onClick={nav.onCompose} disabled={!nav.selectedKontoId} style={{ width: '100%' }}>Neue E-Mail</ImmoButton>
    </div>
  );
}

function EmailOrdnerNavPostfach({ nav, onGewaehlt }: { nav: EmailNavState; onGewaehlt?: () => void }) {
  return (
    <div className="flex flex-col" style={{ gap: 4 }}>
      <span style={{ ...ABSCHNITT, paddingTop: 0 }}>Postfach</span>
      {nav.konten.length <= 1 ? (
        <span className="immo-innen flex items-center" style={{ gap: 8, borderRadius: 13, padding: '9px 10px', fontSize: 12, fontWeight: 700 }}>
          <EnvelopeSimple weight="fill" size={15} color="var(--acc)" style={{ flexShrink: 0 }} />
          <span className="truncate">{nav.konten[0]?.anzeigename || nav.konten[0]?.bezeichnung || nav.konten[0]?.email_adresse || '—'}</span>
        </span>
      ) : nav.konten.map(k => (
        <NavZeile key={k.id} art="objekt" icon={EnvelopeSimple} label={k.anzeigename || k.bezeichnung || k.email_adresse}
          aktiv={k.id === nav.selectedKontoId} onClick={() => { nav.onSelectKonto(k.id); onGewaehlt?.(); }} />
      ))}
    </div>
  );
}
